
import { useState, useEffect } from 'react';

interface PublishModalProps {
  appName: string;
  onClose: () => void;
  onUpdateAppName: (name: string) => void;
}

const steps = [
  { label: 'Compiling components', icon: 'ri-code-s-slash-line' },
  { label: 'Generating native code', icon: 'ri-cpu-line' },
  { label: 'Packaging for iOS & Android', icon: 'ri-smartphone-line' },
  { label: 'Uploading to cloud', icon: 'ri-cloud-upload-line' },
  { label: 'App ready!', icon: 'ri-check-double-line' },
];

export default function PublishModal({ appName, onClose, onUpdateAppName }: PublishModalProps) {
  const [phase, setPhase] = useState<'config' | 'building' | 'done'>('config');
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [localName, setLocalName] = useState(appName);
  const [iosChecked, setIosChecked] = useState(true);
  const [androidChecked, setAndroidChecked] = useState(true);
  const [copied, setCopied] = useState(false);

  const fakeQR = 'https://public.readdy.ai/ai/img_res/7b194b1f7cdd2a8492a7ec24f1484462.jpg';

  useEffect(() => {
    if (phase !== 'building') return;
    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + 4;
        setCurrentStep(Math.min(Math.floor(next / 20), steps.length - 1));
        if (next >= 100) {
          clearInterval(interval);
          setTimeout(() => setPhase('done'), 400);
          return 100;
        }
        return next;
      });
    }, 180);
    return () => clearInterval(interval);
  }, [phase]);

  const handleBuild = () => {
    onUpdateAppName(localName);
    setPhase('building');
    setProgress(0);
    setCurrentStep(0);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText('https://appforge.app/preview/my-app-demo');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-8 py-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-rocket-2-line text-2xl"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold">Publish Your App</h3>
              <p className="text-white/80 text-sm">Deploy to iOS &amp; Android instantly</p>
            </div>
          </div>
        </div>

        <div className="p-8">
          {/* Config Phase */}
          {phase === 'config' && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">App Name</label>
                <input
                  type="text"
                  value={localName}
                  onChange={(e) => setLocalName(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Target Platforms</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input type="checkbox" checked={iosChecked} onChange={(e) => setIosChecked(e.target.checked)} className="w-4 h-4 accent-teal-500 cursor-pointer" />
                    <i className="ri-apple-fill text-xl text-gray-700"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">iOS</p>
                      <p className="text-xs text-gray-500">iPhone &amp; iPad — App Store ready</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input type="checkbox" checked={androidChecked} onChange={(e) => setAndroidChecked(e.target.checked)} className="w-4 h-4 accent-teal-500 cursor-pointer" />
                    <i className="ri-android-fill text-xl text-gray-700"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Android</p>
                      <p className="text-xs text-gray-500">All Android devices — Google Play ready</p>
                    </div>
                  </label>
                </div>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex items-start gap-3">
                <i className="ri-qr-code-line text-teal-500 text-xl mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-teal-800">Test on your phone instantly</p>
                  <p className="text-xs text-teal-600 mt-0.5">After publishing, scan the QR code to open your app on any device — no app store needed.</p>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  Cancel
                </button>
                <button
                  onClick={handleBuild}
                  disabled={!iosChecked && !androidChecked}
                  className="flex-1 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg text-sm font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all shadow-lg shadow-teal-500/25 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-rocket-line mr-2"></i>Build &amp; Publish
                </button>
              </div>
            </div>
          )}

          {/* Building Phase */}
          {phase === 'building' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-5 animate-pulse">
                <i className="ri-rocket-2-line text-4xl text-teal-500"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-1">Building Your App</h4>
              <p className="text-sm text-gray-500 mb-6">This takes just a few seconds...</p>
              <div className="mb-6">
                <div className="flex justify-between text-xs font-semibold text-gray-600 mb-2">
                  <span>Progress</span><span>{progress}%</span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
              <div className="space-y-2.5 text-left">
                {steps.map((step, i) => (
                  <div key={i} className={`flex items-center gap-3 transition-all ${i <= currentStep ? 'opacity-100' : 'opacity-30'}`}>
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${i < currentStep ? 'bg-teal-500' : i === currentStep ? 'bg-teal-100 animate-pulse' : 'bg-gray-100'}`}>
                      <i className={`${step.icon} text-sm ${i < currentStep ? 'text-white' : i === currentStep ? 'text-teal-500' : 'text-gray-400'}`}></i>
                    </div>
                    <span className={`text-sm font-medium ${i <= currentStep ? 'text-gray-800' : 'text-gray-400'}`}>{step.label}</span>
                    {i < currentStep && <i className="ri-check-line text-teal-500 ml-auto"></i>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Done Phase */}
          {phase === 'done' && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-double-line text-3xl text-teal-500"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-1">🎉 App Published!</h4>
                <p className="text-sm text-gray-500">Your app is live and ready to use</p>
              </div>

              {/* QR Code */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-5 text-center">
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center justify-center gap-2">
                  <i className="ri-qr-code-line text-teal-500"></i>
                  Scan to open on your phone
                </p>
                <div className="w-36 h-36 mx-auto bg-white rounded-xl border border-gray-200 p-2 shadow-sm overflow-hidden">
                  <img src={fakeQR} alt="QR Code" className="w-full h-full object-cover" />
                </div>
                <p className="text-xs text-gray-400 mt-2">Point your phone camera at this code</p>
              </div>

              {/* Share Link */}
              <div className="mb-5">
                <p className="text-xs font-semibold text-gray-500 mb-1.5">Share Link</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 truncate">
                    https://appforge.app/preview/my-app-demo
                  </div>
                  <button
                    onClick={handleCopy}
                    className="px-3 py-2 bg-teal-500 text-white rounded-lg text-xs font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Download Buttons */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <a href="#" className="flex items-center gap-2 p-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors cursor-pointer">
                  <i className="ri-apple-fill text-xl"></i>
                  <div>
                    <p className="text-xs text-gray-400">Download</p>
                    <p className="text-sm font-semibold">.ipa file</p>
                  </div>
                </a>
                <a href="#" className="flex items-center gap-2 p-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors cursor-pointer">
                  <i className="ri-android-fill text-xl text-green-400"></i>
                  <div>
                    <p className="text-xs text-gray-400">Download</p>
                    <p className="text-sm font-semibold">.apk file</p>
                  </div>
                </a>
              </div>

              <button
                onClick={onClose}
                className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg text-sm font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
